import { useMemo, type ReactElement } from 'react'
import { parseAsString, useQueryState } from 'nuqs'
import {
  isDefined,
  isNotDefined,
  isNotEmpty,
  type AppId,
  type AppScriptId,
  type ConnectMetricsInput,
  type ConnectMetricsOutput,
  type InstallId,
} from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Group, Select, Stack } from '@mantine/core'
import {
  type GetHookResponse,
  translateLabels,
  useConnectApp,
  useConnectInstall,
  useConnectMetrics,
  useConnectVersion,
  useProfile,
  useTranslation,
} from '@front/model'
import { ButtonRefresh, NotReady, Page, TSegmentedControl, type TSegmentedControlOption } from '@components'
import {
  parseConnectAppId,
  parseConnectInstallId,
  parseConnectScriptId,
  CONNECT_MARKETPLACE_HREF,
} from '../integration/util'
import { ConnectAppLogo } from '../integration/ui'
import { useTimeRangeState } from '../integration/monitoring/useTimeRangeState'
import { TimeRangePicker } from '../integration/monitoring/TimeRangePicker'
import { type Timezone } from '../integration/monitoring/timeRange'
import { MetricsChart } from '../integration/monitoring/MetricsChart'
import { MetricsCards } from '../integration/monitoring/MetricsCards'
import { ConnectAccessGuard } from '../integration/access'
import { LogsSection } from './LogsSection'

type InstallMetricsView = 'metrics' | 'logs'

const VIEW_OPTIONS: Required<TSegmentedControlOption<InstallMetricsView>>[] = [
  {
    label: 'Metrics',
    value: 'metrics',
    icon: 'monitoring',
  },
  {
    label: 'Logs',
    value: 'logs',
    icon: 'receipt_long',
  },
]

// Metrics are Lambda totals across every company, so only Terros (tantalim) users get that view.
function resolveInstallMetricsView(value: string, isTantalim: boolean): InstallMetricsView {
  if (value === 'logs' || !isTantalim) return 'logs'
  return 'metrics'
}

function InstallMetricsPage(): ReactElement {
  const { appId: rawAppId, installId: rawInstallId } = Route.useParams()
  const appId = parseConnectAppId(rawAppId)
  const installId = parseConnectInstallId(rawInstallId)

  if (!appId || !installId) {
    return (
      <ConnectAccessGuard>
        <NotReady error='Invalid metrics path' id='metrics' />
      </ConnectAccessGuard>
    )
  }

  return <InstallMetricsContent appId={appId} installId={installId} />
}

type ContentProps = {
  appId: AppId
  installId: InstallId
}

function InstallMetricsContent({ appId, installId }: ContentProps): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const installState = useConnectInstall(installId)
  const install = installState.data

  const appState = useConnectApp(appId)
  const app = appState.data?.app
  const appName = app?.name

  const versionState = useConnectVersion(install?.appId, install?.appVersion, isDefined(install))
  const versionScripts = versionState.data?.scripts

  const scriptOptions: Array<{ value: AppScriptId; label: string }> = useMemo(
    () =>
      (install?.scripts ?? []).map((s) => {
        const name = versionScripts?.find((vs) => vs.scriptId === s.scriptId)?.name
        return { value: s.scriptId, label: name ?? s.slug ?? s.scriptId }
      }),
    [install?.scripts, versionScripts]
  )

  const [selectedScriptId, setSelectedScriptId] = useQueryState('scriptId', parseAsString)
  const [view, setView] = useQueryState('view', parseAsString.withDefault('metrics'))
  const { endTime, refresh, selection, setSelection, setTimezone, startTime, timezone } = useTimeRangeState()
  const { isTantalim } = useProfile()

  function handleSelectedScriptChange(value: string | null): void {
    setSelectedScriptId(value)
  }

  function handleViewChange(value: InstallMetricsView): void {
    setView(value)
  }

  const queryScriptId = parseConnectScriptId(selectedScriptId)
  const defaultScriptId = scriptOptions[0]?.value
  const resolvedScriptId = queryScriptId ?? defaultScriptId

  const resolvedView = resolveInstallMetricsView(view, isTantalim)
  const metricsInput = useMemo((): ConnectMetricsInput | undefined => {
    if (resolvedView !== 'metrics' || isNotDefined(resolvedScriptId)) return
    return { installId, scriptId: resolvedScriptId, startTime, endTime }
  }, [installId, resolvedView, resolvedScriptId, startTime, endTime])
  const metricsState = useConnectMetrics(metricsInput)

  const breadcrumbs = useMemo(
    () => [
      {
        href: CONNECT_MARKETPLACE_HREF,
        label: t('Marketplace'),
      },
      { disabled: true, href: '#', label: t('Metrics') },
    ],
    [t]
  )

  if (!install) {
    return (
      <ConnectAccessGuard>
        <Page>
          <Page.Content>
            <NotReady error={installState.error} id='install' isLoading={installState.isLoading} />
          </Page.Content>
        </Page>
      </ConnectAccessGuard>
    )
  }

  return (
    <ConnectAccessGuard>
      <Page>
        <Page.Heading
          back
          breadcrumbs={breadcrumbs}
          handleGoBack={() => navigate({ to: CONNECT_MARKETPLACE_HREF })}
          leftSection={
            app && (
              <ConnectAppLogo avatarBlurhash={app.avatarBlurhash} avatarUrl={app.avatarUrl} name={app.name} size='sm' />
            )
          }
          title={t('{{appName}} Metrics', { appName: appName ?? appId })}
        />
        <Page.FunctionBar className='w-full'>
          <Stack w='100%'>
            {isNotEmpty(scriptOptions) ? (
              <Select
                data={scriptOptions}
                label={t('Script')}
                value={resolvedScriptId}
                onChange={handleSelectedScriptChange}
                maw={360}
                w='100%'
              />
            ) : null}
            <Group align='center' className='pt-md min-w-0' justify='space-between' wrap='wrap'>
              {isTantalim && (
                <TSegmentedControl
                  options={translateLabels(VIEW_OPTIONS, t)}
                  value={resolvedView}
                  onChange={handleViewChange}
                />
              )}
              <Group align='center' className='max-w-full min-w-0' gap='sm' wrap='wrap'>
                <TimeRangePicker
                  selection={selection}
                  timezone={timezone}
                  onChange={setSelection}
                  onTimezoneChange={setTimezone}
                />
                <ButtonRefresh isFetching={metricsState.isLoading} refetch={refresh} />
              </Group>
            </Group>
          </Stack>
        </Page.FunctionBar>
        <Page.Content scrollArea>
          <Stack gap='lg'>
            {resolvedView === 'metrics' && (
              <MetricsView metricsState={metricsState} startTime={startTime} endTime={endTime} timezone={timezone} />
            )}

            {resolvedView === 'logs' && (
              <LogsSection
                endTime={endTime}
                installId={installId}
                scriptId={resolvedScriptId}
                startTime={startTime}
                timezone={timezone}
              />
            )}
          </Stack>
        </Page.Content>
      </Page>
    </ConnectAccessGuard>
  )
}

type MetricsState = GetHookResponse<ConnectMetricsOutput>

type MetricsViewProps = {
  metricsState: MetricsState
  startTime: number
  endTime: number
  timezone: Timezone
}

function MetricsView({ metricsState, startTime, endTime, timezone }: MetricsViewProps): ReactElement {
  const metrics = metricsState.data

  if (metricsState.isLoading) return <NotReady isLoading id='metrics' />

  if (metricsState.error) return <NotReady error={metricsState.error} id='metrics' />

  // The request is scoped to one script, so the response holds at most one entry.
  const script = metrics?.type === 'success' ? metrics.scripts[0] : undefined
  if (!isDefined(script)) return <NotReady error='No metrics found' id='metrics' />

  return (
    <>
      <MetricsChart datapoints={script.datapoints} startTime={startTime} endTime={endTime} timezone={timezone} />
      <MetricsCards invocations={script.invocations} errors={script.errors} avgDurationMs={script.avgDurationMs} />
    </>
  )
}

export const Route = createFileRoute('/integration/marketplace_/$appId/$installId_/metrics/')({
  component: InstallMetricsPage,
})
